import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from '@/providers/RouterShim';
import useBranch from '../../../../../data/branch/useBranch';
import useBranches from '../../../../../data/branch/useBranches';
import useBranchHierachy from '../../../../../data/branch/useBranchHierarchy';
import useAuth from '../../../../../hooks/useAuth';
import { useTemporaryAccess } from '../../../../../providers/auth';
import Alert from '../../../../../styled-components/components/Alert';
import Avatar from '../../../../../styled-components/components/Avatar';
import { IconArrowElbowDownRight } from '../../../../../styled-components/components/Icon';
import InputSearch from '../../../../../styled-components/components/InputSearch';
import Loader from '../../../../../styled-components/components/Loader';
import ScopeAvatar from '../../../../../styled-components/components/ScopeAvatar';
import { Skeleton } from '../../../../../styled-components/components/Skeleton';
import { urlToCfit } from '../../../../../utils/cfit';
import { getInitials } from '../../../../../utils/initials';
import useSearchAdminBranches from './useSearchAdminBranches';
import useSearchBranches from './useSearchBranches';
import {
  Backdrop,
  Body,
  Dialog,
  Header,
  HeaderHierachy,
  HeaderHierachyTitle,
  HeaderName,
  HeaderTitle,
  Option,
  OptionDummy,
  OptionTitle,
  Search,
  Wrapper,
} from './wrapper';

interface ScopeFoldoutProps {
  onClose: () => void;
}

const ScopeFoldout = ({ onClose }: ScopeFoldoutProps) => {
  const { t } = useTranslation(['header', 'validation']);
  const { branchId } = useParams();
  const { isAdmin, decodedToken } = useAuth();
  const [branch] = useBranch();
  const [branches, requestBranches] = useBranches();
  const [hierarchy] = useBranchHierachy();
  const navigate = useNavigate();
  const location = useLocation();
  const [addTemporaryAccess] = useTemporaryAccess();
  const [isGrantingAccess, setIsGrantingAccess] = useState(false);

  const fallbackBranch = useMemo(
    () => (branchId ? branches?.find((x) => x.id === branchId) : undefined),
    [branches, branchId],
  );

  const selectedBranch = branch || fallbackBranch;

  const { control, register, setFocus } = useForm<{ search: '' }>({
    defaultValues: {
      search: '',
    },
  });

  useEffect(() => {
    setFocus('search');
  }, []);

  const search = useWatch({ control, name: 'search' });

  const [list, request, query] = useSearchAdminBranches(search);
  const [filteredBranches] = useSearchBranches(search);

  const onSelectBranch = useCallback(
    async (id: string) => {
      // Check if user already has access to this branch
      const belongsToBranch = decodedToken?.['belongs-to-branch'];
      const hasAccess = Array.isArray(belongsToBranch) && belongsToBranch.includes(id);

      if (!hasAccess && isAdmin) {
        // Need to get temporary access first before navigating
        setIsGrantingAccess(true);
        try {
          await addTemporaryAccess(id);
        } catch {
          // Error is already handled by useTemporaryAccess (shows snackbar)
          setIsGrantingAccess(false);
          return;
        }
        setIsGrantingAccess(false);
      }

      // Now safe to navigate
      onClose();
      let splitted = location.pathname.split('/');
      splitted[1] = id;

      // Determine truncation index based on route type
      // - settings: keep up to 5 segments
      // - leads/lead, customers/contacts, customers/businesses: truncate to 3 (parent list)
      // - others: keep up to 4 segments
      let index: number;
      if (splitted[2] === 'settings') {
        index = 5;
      } else if (
        (splitted[2] === 'leads' &&
          (splitted[3] === 'lead' ||
            splitted[3] === 'conversation' ||
            splitted[3] === 'online-order')) ||
        (splitted[2] === 'customers' &&
          (splitted[3] === 'contacts' || splitted[3] === 'businesses'))
      ) {
        index = 3;
      } else {
        index = 4;
      }

      if (splitted.length > index) {
        splitted = splitted.slice(0, index);
      }
      navigate(splitted.join('/'));
    },
    [location, navigate, onClose, decodedToken, isAdmin, addTemporaryAccess],
  );

  const image = selectedBranch?.logo ? urlToCfit(selectedBranch.logo, { width: 225 }) : undefined;

  return (
    <Wrapper>
      <Backdrop onClick={onClose} aria-label={t('scopeSelect.closeButton', { ns: 'header' })} />
      <Dialog>
        <Header>
          {requestBranches.isLoading ? (
            <ScopeAvatar size="large" loading />
          ) : (
            <ScopeAvatar
              size="large"
              image={
                selectedBranch && image
                  ? {
                      src: image,
                      alt: selectedBranch?.name,
                    }
                  : undefined
              }
              initials={!selectedBranch?.logo ? getInitials(selectedBranch?.name) || '' : undefined}
            />
          )}
          <HeaderName>
            {requestBranches.isLoading ? (
              <Skeleton $width="100px" $height="20px" />
            ) : (
              <>
                <HeaderTitle>
                  {selectedBranch?.name || t('unknown', { ns: 'validation' })}
                </HeaderTitle>
                {hierarchy && hierarchy?.id !== branch?.id && hierarchy?.name && (
                  <HeaderHierachy
                    onClick={() => onSelectBranch(hierarchy.id)}
                    title={t('scopeSelect.selectParentOrganization', {
                      ns: 'header',
                      option: hierarchy.name,
                    })}
                  >
                    <IconArrowElbowDownRight />
                    <HeaderHierachyTitle>{hierarchy.name}</HeaderHierachyTitle>
                  </HeaderHierachy>
                )}
              </>
            )}
          </HeaderName>
        </Header>
        <Body>
          <Search>
            <InputSearch
              small
              id="branch-list-search"
              label={t('scopeSelect.search.label', { ns: 'header' })}
              placeholder={t('scopeSelect.search.placeholder', { ns: 'header' })}
              autoComplete="off"
              {...register('search')}
            />
          </Search>
          {isGrantingAccess ? (
            <OptionDummy>
              <Loader />
            </OptionDummy>
          ) : isAdmin && search && query ? (
            request.isLoading || search !== query ? (
              <OptionDummy>
                <Loader />
              </OptionDummy>
            ) : request.isError ? (
              <OptionDummy>
                <Alert tone="error" message={t('scopeSelect.alert.error', { ns: 'header' })} />
              </OptionDummy>
            ) : !list?.length ? (
              <OptionDummy>
                <Alert
                  tone="warning"
                  message={t('scopeSelect.alert.empty', { ns: 'header', term: query })}
                />
              </OptionDummy>
            ) : (
              list?.map((b) => (
                <Option
                  key={b.id}
                  onClick={() => onSelectBranch(b.id)}
                  title={t('scopeSelect.selectBranch', { ns: 'header', option: b.name })}
                  $selected={selectedBranch?.id === b.id}
                >
                  <Avatar size="small" initials={getInitials(b.name) || ''} />
                  <OptionTitle>{b?.name || t('unknown', { ns: 'validation' })}</OptionTitle>
                </Option>
              ))
            )
          ) : search && !filteredBranches?.length ? (
            <OptionDummy>
              <Alert
                tone="warning"
                message={t('scopeSelect.alert.empty', { ns: 'header', term: query })}
              />
            </OptionDummy>
          ) : (
            filteredBranches?.map((b) => {
              const logo = b?.logo ? urlToCfit(b.logo, { width: 225 }) : undefined;
              return (
                <Option
                  key={b.id}
                  onClick={() => onSelectBranch(b.id)}
                  title={t('scopeSelect.selectBranch', { ns: 'header', option: b.name })}
                  $selected={selectedBranch?.id === b.id}
                >
                  <ScopeAvatar
                    size="small"
                    image={
                      logo
                        ? {
                            src: logo,
                            alt: b.name,
                          }
                        : undefined
                    }
                    initials={!b?.logo ? getInitials(b.name) || '' : undefined}
                  />
                  <OptionTitle>{b?.name || t('unknown', { ns: 'validation' })}</OptionTitle>
                </Option>
              );
            })
          )}
        </Body>
      </Dialog>
    </Wrapper>
  );
};

export default ScopeFoldout;
